'use client';

import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Dim,
  DropdownTriggerArrowCloseIconButton,
  DropdownTriggerArrowOpenIconButton,
  DropdownTriggerBox,
  DropdownTriggerInput,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  SheetTrigger,
  Typography,
} from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';
import { usePreventBodyScroll } from '../../common/hooks';
import { PLATFORMS } from '../../join/funnels/SNSFunnel/constants/platforms';
import type { SNSLinkEditFormData } from './SNSLinkEditForm';

const platformItemVariants = tv({
  base: 'py-[11px] cursor-pointer',
  variants: {
    selected: {
      true: 'text-black',
      false: 'text-gray-600',
    },
  },
  defaultVariants: {
    selected: false,
  },
});

interface Props {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}

export const SNSPlatformSelector = ({
  value,
  onChange,
  className,
  placeholder = '플랫폼을 선택하세요',
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const dialog = useDialog();
  const { watch } = useFormContext<SNSLinkEditFormData>();

  // 실시간으로 snsLinks 값을 감시
  const snsLinks = watch('snsLinks') || [];

  usePreventBodyScroll({ open: isOpen });

  const selectedPlatform = PLATFORMS.find(
    (platform) => platform.id.toLocaleUpperCase() === value.toLocaleUpperCase(),
  );

  const onPlatformSelect = (platformId: string) => {
    // 중복 플랫폼 체크 - 현재 선택된 값과 다르면서 이미 존재하는 플랫폼인지 확인
    const isDuplicate = snsLinks.some(
      (link) =>
        link.platform.toLocaleUpperCase() === platformId.toLocaleUpperCase() &&
        link.platform.toLocaleUpperCase() !== value.toLocaleUpperCase(),
    );

    if (isDuplicate) {
      setIsOpen(false);
      // NOTE: 중복 플랫폼 선택 시 다이얼로그 표시 후 드롭다운을 다시 열기 위해 zero-timeout
      setTimeout(() => {
        setIsOpen(false);
      }, 0);

      dialog.open({
        title: '플랫폼 별 1개의 정보만 제출 가능합니다.',
        confirmButtonText: '확인',
        onConfirm: () => {
          setIsOpen(true);
        },
      });
      return;
    }

    onChange(platformId);
    setIsOpen(false);
  };

  return (
    <div className={twMerge('', className)}>
      <Sheet
        direction="bottom"
        open={isOpen}
        onOpenChange={() => {
          setIsOpen(!isOpen);
        }}
      >
        <SheetTrigger
          asChild
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
        >
          <DropdownTriggerBox>
            <DropdownTriggerInput placeholder={placeholder} value={selectedPlatform?.name || ''} />
            <DropdownTriggerArrowOpenIconButton />
            <DropdownTriggerArrowCloseIconButton />
          </DropdownTriggerBox>
        </SheetTrigger>
        <SheetPortal>
          <SheetContent
            onPointerDownOutside={() => {
              setIsOpen(false);
            }}
          >
            <SheetMotion direction="bottom">
              <BottomSheetTitle>
                <BottomSheetHandleArea />
                <BottomSheetTitleContainer>
                  <BottomSheetTitleBox>플랫폼 선택</BottomSheetTitleBox>
                  <IconButton size="size28x28" onClick={() => setIsOpen(false)}>
                    <IcDelete strokeColor="black" />
                  </IconButton>
                </BottomSheetTitleContainer>
              </BottomSheetTitle>

              <div className="flex flex-col px-4 bg-white safe-area-bottom max-h-[532px] overflow-y-auto">
                {PLATFORMS.map((platform) => {
                  const isSelected = selectedPlatform?.id === platform.id;

                  return (
                    <Typography
                      key={platform.id}
                      className={platformItemVariants({ selected: isSelected })}
                      colorScheme={isSelected ? 'black' : 'gray-600'}
                      variant="body13pxReg"
                      onClick={() => onPlatformSelect(platform.id)}
                    >
                      {platform.name}
                    </Typography>
                  );
                })}
              </div>
            </SheetMotion>
          </SheetContent>
          <SheetDimMotion>
            <Dim />
          </SheetDimMotion>
        </SheetPortal>
      </Sheet>
    </div>
  );
};
